// apps/web/src/features/admin/form-config/shared/FormulaTestPanel.tsx
//
// P4 T5 — client-side formula dry-run tester. The rules engine is isomorphic
// (DOM-free, already bundled for blur evaluation), so this evaluates entirely
// in-browser — no server round-trip (unlike the transform-plugin TestPanel).
//
// It scaffolds an input for every SIMPLE `{field}` ref in the formula
// (prefilled from the sample-data heuristics) and evaluates against those.
// Dotted refs (line-item / txn / const / user / batch / document) are NOT
// simulated in v1: they're listed with a note and evaluated with empty
// contexts, so the tester never pretends to know a value it can't.

'use client';

import { useEffect, useMemo, useState } from 'react';
import { parse, evaluateExpression, collectFieldRefs } from '@capture/rules-engine';
import { sampleValueForFieldName } from '@capture/shared';
import { api } from '@/lib/api-client';
import './FormulaTestPanel.css';

interface FormulaTestPanelProps {
  formula: string;
}

interface EvalOk {
  ok: true;
  value: string | number | boolean | null;
}
interface EvalErr {
  ok: false;
  error: string;
}

/** Numeric-looking input becomes a number; everything else stays a string. */
function coerceInput(raw: string): string | number {
  const t = raw.trim();
  if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return raw;
}

function describeType(v: string | number | boolean | null): string {
  return v === null ? 'null' : typeof v;
}

function displayValue(v: string | number | boolean | null): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return `"${v}"`;
  return String(v);
}

export function FormulaTestPanel({ formula }: FormulaTestPanelProps) {
  // Parse once per formula to discover refs. A parse failure here just means
  // no scaffolding — Evaluate still runs and surfaces the parser message.
  const refs = useMemo(() => {
    try {
      return collectFieldRefs(parse(formula));
    } catch {
      return null;
    }
  }, [formula]);

  const simpleRefs = useMemo(() => (refs ?? []).filter((r) => !r.includes('.')), [refs]);
  const dottedRefs = useMemo(() => (refs ?? []).filter((r) => r.includes('.')), [refs]);

  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [result, setResult] = useState<EvalOk | EvalErr | null>(null);

  // S-4 — the tenant holiday calendar, fetched once per mount so
  // NETWORKDAYS/WORKDAY here match server evaluation instead of being
  // silently weekends-only. `?context=1` asks for the transformed
  // { dates, recurringMonthDay } shape (the plain GET returns the raw admin
  // row list for the settings table). Fetch failure leaves this undefined —
  // the evaluator falls back to weekends-only, it never blocks Evaluate.
  const [holidays, setHolidays] = useState<
    { dates: string[]; recurringMonthDay: string[] } | undefined
  >(undefined);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ dates: string[]; recurringMonthDay: string[] }>('/admin/holidays?context=1')
      .then((h) => {
        if (!cancelled) setHolidays(h);
      })
      .catch(() => {
        /* fetch failure → undefined holidays, weekends-only fallback — never blocks Evaluate */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefill newly-discovered simple refs from the sample-data heuristics;
  // never clobber a value the user already typed.
  useEffect(() => {
    setInputs((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const name of simpleRefs) {
        if (!(name in next)) {
          next[name] = String(sampleValueForFieldName(name));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [simpleRefs]);

  function runEvaluate() {
    const fields: Record<string, string | number> = {};
    for (const name of simpleRefs) {
      fields[name] = coerceInput(inputs[name] ?? '');
    }
    try {
      const value = evaluateExpression(formula, { fields, holidays });
      setResult({ ok: true, value });
    } catch (e) {
      setResult({ ok: false, error: (e as Error).message });
    }
  }

  return (
    <div className="formula-test-panel" data-testid="formula-test-panel">
      {simpleRefs.length > 0 && (
        <div className="formula-test-inputs">
          {simpleRefs.map((name) => (
            <label key={name} className="formula-test-field">
              <span className="formula-test-field-name">{name}</span>
              <input
                className="formula-test-input"
                data-testid={`formula-test-input-${name}`}
                aria-label={`Test value for ${name}`}
                value={inputs[name] ?? ''}
                onChange={(e) => setInputs((prev) => ({ ...prev, [name]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      )}

      {dottedRefs.length > 0 && (
        <p className="formula-test-unsimulated" data-testid="formula-test-unsimulated">
          Not simulated in the tester (v1): {dottedRefs.join(', ')} — line-item, transaction,
          constant, and user references evaluate against empty contexts here.
        </p>
      )}

      <button
        type="button"
        className="formula-test-run"
        data-testid="formula-test-run"
        onClick={runEvaluate}
      >
        Evaluate
      </button>

      {result && (
        <div
          className={`formula-test-result ${result.ok ? 'is-ok' : 'is-error'}`}
          data-testid="formula-test-result"
          role={result.ok ? undefined : 'alert'}
        >
          {result.ok ? (
            <>
              <span className="formula-test-result-value">{displayValue(result.value)}</span>
              <span className="formula-test-result-type">{describeType(result.value)}</span>
            </>
          ) : (
            <span className="formula-test-result-error">{result.error}</span>
          )}
        </div>
      )}
    </div>
  );
}
